<template>
  <div class="cs-tickets-container pa-4 pa-md-6">
    <!-- Header -->
    <div class="d-flex flex-wrap align-center justify-space-between mb-5">
      <div>
        <h1 class="text-h5 font-weight-bold navy--text mb-1">
          <v-icon color="#082C69" class="me-2" size="28">mdi-ticket-account</v-icon>
          التذاكر والشكاوى والتصعيدات
        </h1>
        <p class="text-caption text-md-body-2 text-muted mb-0">
          متابعة جميع شكاوى العملاء وطلباتهم واستفساراتهم وتصعيداتهم عبر الأقسام ذات العلاقة
        </p>
      </div>

      <div class="d-flex align-center mt-3 mt-sm-0">
        <v-btn
          depressed
          outlined
          color="#082C69"
          class="rounded-lg font-weight-bold me-2"
          :loading="exportLoading"
          @click="exportCsv"
        >
          <v-icon left size="18">mdi-file-delimited-outline</v-icon>
          تصدير CSV
        </v-btn>

        <v-btn
          depressed
          color="#082C69"
          class="white--text rounded-lg font-weight-bold"
          @click="fetchTickets"
          :loading="loading"
        >
          <v-icon left size="18">mdi-refresh</v-icon>
          تحديث
        </v-btn>
      </div>
    </div>

    <!-- 6 Top KPI Cards -->
    <v-row dense class="mb-5 kpi-grid">
      <!-- 1. إجمالي التذاكر المفتوحة -->
      <v-col cols="12" sm="6" md="4" lg="2">
        <v-card
          class="kpi-card pa-3 rounded-xl elevation-1 cursor-pointer"
          :class="{ 'kpi-card-active': filters.status === 'open_all' }"
          @click="filterByKpi('open_all')"
        >
          <div class="d-flex align-center justify-space-between mb-1">
            <span class="text-caption font-weight-bold text-muted">التذاكر المفتوحة</span>
            <v-avatar size="32" color="#E0F2FE">
              <v-icon size="18" color="#0369A1">mdi-ticket-outline</v-icon>
            </v-avatar>
          </div>
          <div class="text-h5 font-weight-bold navy--text">{{ summary.total_open }}</div>
          <div class="text-caption text-muted">قيد المتابعة والمعالجة</div>
        </v-card>
      </v-col>

      <!-- 2. شكاوى عالية الأولوية -->
      <v-col cols="12" sm="6" md="4" lg="2">
        <v-card
          class="kpi-card pa-3 rounded-xl elevation-1 cursor-pointer"
          :class="{ 'kpi-card-active': filters.priority === 'urgent' }"
          @click="filterByKpi('priority_urgent')"
        >
          <div class="d-flex align-center justify-space-between mb-1">
            <span class="text-caption font-weight-bold text-muted">عالية الأولوية</span>
            <v-avatar size="32" color="#FEE2E2">
              <v-icon size="18" color="#B91C1C">mdi-alert-circle-outline</v-icon>
            </v-avatar>
          </div>
          <div class="text-h5 font-weight-bold text-danger">{{ summary.high_priority }}</div>
          <div class="text-caption text-muted">عاجلة / عالية</div>
        </v-card>
      </v-col>

      <!-- 3. طلبات قيد المتابعة -->
      <v-col cols="12" sm="6" md="4" lg="2">
        <v-card
          class="kpi-card pa-3 rounded-xl elevation-1 cursor-pointer"
          :class="{ 'kpi-card-active': filters.status === 'in_progress' }"
          @click="filterByKpi('status_in_progress')"
        >
          <div class="d-flex align-center justify-space-between mb-1">
            <span class="text-caption font-weight-bold text-muted">قيد المتابعة</span>
            <v-avatar size="32" color="#EFF6FF">
              <v-icon size="18" color="#1D4ED8">mdi-progress-clock</v-icon>
            </v-avatar>
          </div>
          <div class="text-h5 font-weight-bold" style="color: #1D4ED8">{{ summary.in_progress }}</div>
          <div class="text-caption text-muted">جاري العمل عليها</div>
        </v-card>
      </v-col>

      <!-- 4. تصعيدات إلى المالية -->
      <v-col cols="12" sm="6" md="4" lg="2">
        <v-card
          class="kpi-card pa-3 rounded-xl elevation-1 cursor-pointer"
          :class="{ 'kpi-card-active': filters.department === 'finance' }"
          @click="filterByKpi('dept_finance')"
        >
          <div class="d-flex align-center justify-space-between mb-1">
            <span class="text-caption font-weight-bold text-muted">تصعيد للمالية</span>
            <v-avatar size="32" color="#FEF3C7">
              <v-icon size="18" color="#B45309">mdi-currency-usd</v-icon>
            </v-avatar>
          </div>
          <div class="text-h5 font-weight-bold text-amber-darken-3">{{ summary.escalated_finance }}</div>
          <div class="text-caption text-muted">قضايا ومطالبات مالية</div>
        </v-card>
      </v-col>

      <!-- 5. تصعيدات إلى العمليات -->
      <v-col cols="12" sm="6" md="4" lg="2">
        <v-card
          class="kpi-card pa-3 rounded-xl elevation-1 cursor-pointer"
          :class="{ 'kpi-card-active': filters.department === 'operations' }"
          @click="filterByKpi('dept_operations')"
        >
          <div class="d-flex align-center justify-space-between mb-1">
            <span class="text-caption font-weight-bold text-muted">تصعيد للعمليات</span>
            <v-avatar size="32" color="#F3E8FF">
              <v-icon size="18" color="#7E22CE">mdi-truck-fast-outline</v-icon>
            </v-avatar>
          </div>
          <div class="text-h5 font-weight-bold" style="color: #7E22CE">{{ summary.escalated_operations }}</div>
          <div class="text-caption text-muted">رحلات وسائقين ومحطات</div>
        </v-card>
      </v-col>

      <!-- 6. تم الحل اليوم -->
      <v-col cols="12" sm="6" md="4" lg="2">
        <v-card
          class="kpi-card pa-3 rounded-xl elevation-1 cursor-pointer"
          :class="{ 'kpi-card-active': filters.status === 'resolved' }"
          @click="filterByKpi('status_resolved')"
        >
          <div class="d-flex align-center justify-space-between mb-1">
            <span class="text-caption font-weight-bold text-muted">تم الحل اليوم</span>
            <v-avatar size="32" color="#DCFCE7">
              <v-icon size="18" color="#15803D">mdi-check-decagram-outline</v-icon>
            </v-avatar>
          </div>
          <div class="text-h5 font-weight-bold text-emerald">{{ summary.resolved_today }}</div>
          <div class="text-caption text-muted">تذاكر مغلقة بنجاح</div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Filters Section -->
    <v-card class="elevation-1 rounded-xl pa-4 mb-5 filter-card">
      <v-row dense align="center">
        <!-- Search -->
        <v-col cols="12" md="4">
          <v-text-field
            v-model="filters.search"
            dense
            outlined
            hide-details
            clearable
            placeholder="بحث برقم التذكرة أو اسم العميل أو الهاتف أو الحجز..."
            prepend-inner-icon="mdi-magnify"
            @keydown.enter="applyFilters"
            @click:clear="filters.search = ''; $nextTick(() => applyFilters())"
          ></v-text-field>
        </v-col>

        <!-- Status -->
        <v-col cols="12" sm="6" md="2">
          <v-select
            v-model="filters.status"
            :items="statusOptions"
            dense
            outlined
            hide-details
            label="الحالة"
            item-text="text"
            item-value="value"
            @change="applyFilters"
          ></v-select>
        </v-col>

        <!-- Category -->
        <v-col cols="12" sm="6" md="2">
          <v-select
            v-model="filters.category"
            :items="categoryOptions"
            dense
            outlined
            hide-details
            label="التصنيف"
            item-text="text"
            item-value="value"
            @change="applyFilters"
          ></v-select>
        </v-col>

        <!-- Priority -->
        <v-col cols="12" sm="6" md="2">
          <v-select
            v-model="filters.priority"
            :items="priorityOptions"
            dense
            outlined
            hide-details
            label="الأولوية"
            item-text="text"
            item-value="value"
            @change="applyFilters"
          ></v-select>
        </v-col>

        <!-- Department -->
        <v-col cols="12" sm="6" md="2">
          <v-select
            v-model="filters.department"
            :items="departmentOptions"
            dense
            outlined
            hide-details
            label="الجهة المعنية"
            item-text="text"
            item-value="value"
            @change="applyFilters"
          ></v-select>
        </v-col>

        <!-- Channel -->
        <v-col cols="12" sm="6" md="2" class="mt-2">
          <v-select
            v-model="filters.channel"
            :items="channelOptions"
            dense
            outlined
            hide-details
            label="القناة"
            item-text="text"
            item-value="value"
            @change="applyFilters"
          ></v-select>
        </v-col>

        <!-- Assigned Staff -->
        <v-col cols="12" sm="6" md="3" class="mt-2">
          <v-select
            v-model="filters.assigned_to"
            :items="staffOptions"
            dense
            outlined
            hide-details
            label="الموظف المسؤول"
            item-text="name"
            item-value="id"
            @change="applyFilters"
          ></v-select>
        </v-col>

        <!-- Date Range -->
        <v-col cols="12" sm="6" md="2" class="mt-2">
          <v-text-field
            v-model="filters.date_from"
            type="date"
            dense
            outlined
            hide-details
            label="من تاريخ"
            @change="applyFilters"
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="6" md="2" class="mt-2">
          <v-text-field
            v-model="filters.date_to"
            type="date"
            dense
            outlined
            hide-details
            label="إلى تاريخ"
            @change="applyFilters"
          ></v-text-field>
        </v-col>

        <!-- Action Buttons -->
        <v-col cols="12" md="3" class="mt-2 d-flex align-center justify-end">
          <v-btn
            depressed
            color="#082C69"
            class="white--text font-weight-bold me-2 px-4"
            @click="applyFilters"
          >
            <v-icon left size="16">mdi-filter-check-outline</v-icon>
            تطبيق
          </v-btn>
          <v-btn
            depressed
            outlined
            color="#64748B"
            class="font-weight-bold px-3"
            @click="resetFilters"
          >
            إعادة ضبط
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- Tickets Table Section -->
    <v-card class="elevation-1 rounded-xl mb-6 content-card">
      <div class="d-flex align-center justify-space-between px-4 py-3 border-bottom">
        <div class="d-flex align-center">
          <v-icon color="#082C69" class="me-2">mdi-format-list-bulleted</v-icon>
          <span class="text-subtitle-1 font-weight-bold navy--text">قائمة التذاكر والشكاوى</span>
          <span class="ticket-count-badge ms-2">
            {{ totalItems }} تذكرة
          </span>
        </div>
        <div v-if="selectedTicket" class="text-caption text-muted">
          التذكرة المحددة حالياً:
          <span class="mono-code font-weight-bold navy--text">{{ selectedTicket.ticket_code }}</span>
        </div>
      </div>

      <v-data-table
        :headers="tableHeaders"
        :items="tickets"
        :loading="loading"
        :server-items-length="totalItems"
        :items-per-page="perPage"
        :page="currentPage"
        hide-default-footer
        class="elevation-0 tickets-table"
        no-data-text="لا توجد تذاكر أو شكاوى مطابقة لمعايير البحث"
        loading-text="جاري تحميل التذاكر..."
        @click:row="selectTicket"
      >
        <!-- # Index -->
        <template v-slot:item.index="{ index }">
          {{ (currentPage - 1) * perPage + index + 1 }}
        </template>

        <!-- Ticket Code -->
        <template v-slot:item.ticket_code="{ item }">
          <div class="d-flex align-center">
            <span
              v-if="attentionTicketIds.includes(item.id)"
              class="attention-badge me-1"
              title="تحديث أو رد جديد"
            >
              جديد
            </span>
            <span class="mono-code font-weight-bold navy--text">{{ item.ticket_code }}</span>
          </div>
        </template>

        <!-- Customer -->
        <template v-slot:item.customer_name="{ item }">
          <div>
            <div class="font-weight-bold text-body-2 text-dark">{{ item.customer_name }}</div>
            <div class="text-caption font-weight-medium mono-code" style="color: #475569;">{{ item.customer_phone }}</div>
          </div>
        </template>

        <!-- Booking Ref -->
        <template v-slot:item.booking_ref="{ item }">
          <span class="mono-code text-caption font-weight-bold navy--text">{{ item.booking_ref }}</span>
        </template>

        <!-- Route & Trip -->
        <template v-slot:item.route_name="{ item }">
          <div>
            <div class="text-body-2 font-weight-bold text-dark">{{ item.route_name }}</div>
            <span class="text-caption font-weight-medium mono-code" style="color: #475569;">{{ item.trip_ref }}</span>
          </div>
        </template>

        <!-- Category -->
        <template v-slot:item.category="{ item }">
          <span
            class="category-badge"
            :style="getCategoryBadgeStyle(item.category)"
          >
            {{ item.category_label }}
          </span>
        </template>

        <!-- Priority -->
        <template v-slot:item.priority="{ item }">
          <span
            class="badge-chip"
            :style="{ backgroundColor: item.priority_color.bg, color: item.priority_color.text }"
          >
            {{ item.priority_label }}
          </span>
        </template>

        <!-- Status -->
        <template v-slot:item.status="{ item }">
          <span
            class="badge-chip"
            :style="{ backgroundColor: item.status_color.bg, color: item.status_color.text }"
          >
            {{ item.status_label }}
          </span>
        </template>

        <!-- Department -->
        <template v-slot:item.department="{ item }">
          <v-chip x-small outlined color="#475569" class="font-weight-bold">
            {{ item.department_label }}
          </v-chip>
        </template>

        <!-- Assigned Staff -->
        <template v-slot:item.assigned_name="{ item }">
          <span class="text-caption" :class="item.assigned_to ? 'navy--text font-weight-bold' : 'text-muted'">
            {{ item.assigned_name }}
          </span>
        </template>

        <!-- SLA -->
        <template v-slot:item.sla="{ item }">
          <span
            class="sla-badge"
            :style="getSlaBadgeStyle(item)"
          >
            <v-icon x-small :color="getSlaBadgeStyle(item).color" class="me-1">
              {{ item.sla_status === 'overdue' ? 'mdi-alert-circle' : (item.sla_status === 'on_track' ? 'mdi-check-circle-outline' : 'mdi-clock-outline') }}
            </v-icon>
            {{ item.sla_due_formatted }}
          </span>
        </template>

        <!-- Actions -->
        <template v-slot:item.actions="{ item }">
          <v-btn
            x-small
            depressed
            :color="selectedTicket && selectedTicket.id === item.id ? '#082C69' : '#E0E7FF'"
            :class="selectedTicket && selectedTicket.id === item.id ? 'white--text' : 'navy--text'"
            class="font-weight-bold rounded-lg px-2"
            @click.stop="selectTicket(item)"
          >
            <v-icon left x-small>mdi-eye-outline</v-icon>
            معاينة
          </v-btn>
        </template>
      </v-data-table>

      <!-- Pagination -->
      <div class="d-flex align-center justify-space-between px-4 py-3 border-top">
        <div class="text-caption text-muted">
          عرض {{ tickets.length }} من إجمالي {{ totalItems }} تذكرة
        </div>
        <v-pagination
          v-if="lastPage > 1"
          v-model="currentPage"
          :length="lastPage"
          :total-visible="7"
          circle
          color="#082C69"
          @input="onPageChange"
        ></v-pagination>
      </div>
    </v-card>

    <!-- Lower Section: Selected Ticket Workspace (Section 15, 16, 17, 18, 19, 21) -->
    <v-fade-transition>
      <div v-if="selectedTicket" ref="workspaceSection" class="selected-ticket-workspace mb-6">
        <!-- Ticket Header Banner -->
        <v-card class="elevation-1 rounded-xl pa-4 mb-4 bg-white border-primary-top">
          <div class="d-flex flex-wrap align-center justify-space-between">
            <div class="d-flex align-center">
              <v-avatar size="44" color="#E0F2FE" class="me-3">
                <v-icon size="24" color="#082C69">mdi-face-agent</v-icon>
              </v-avatar>
              <div>
                <div class="d-flex align-center flex-wrap">
                  <span class="mono-code text-h6 font-weight-bold navy--text me-2">{{ selectedTicket.ticket_code }}</span>
                  <span
                    class="badge-chip me-2"
                    :style="{ backgroundColor: selectedTicket.status_color.bg, color: selectedTicket.status_color.text }"
                  >
                    {{ selectedTicket.status_label }}
                  </span>
                  <span
                    class="badge-chip me-2"
                    :style="{ backgroundColor: selectedTicket.priority_color.bg, color: selectedTicket.priority_color.text }"
                  >
                    {{ selectedTicket.priority_label }}
                  </span>
                  <v-chip small outlined color="#64748B" class="font-weight-bold">
                    {{ selectedTicket.department_label }}
                  </v-chip>
                </div>
                <div class="text-body-2 font-weight-bold mt-1 text-dark">{{ selectedTicket.subject }}</div>
              </div>
            </div>

            <!-- Quick Action Toolbar -->
            <div class="d-flex flex-wrap align-center gap-2 mt-3 mt-md-0">
              <v-btn small depressed color="#082C69" class="white--text font-weight-bold rounded-lg" @click="openStatusModal">
                <v-icon left x-small>mdi-list-status</v-icon>
                تغيير الحالة
              </v-btn>

              <v-btn small depressed outlined color="#082C69" class="font-weight-bold rounded-lg" @click="openAssignModal">
                <v-icon left x-small>mdi-account-arrow-right-outline</v-icon>
                تعيين موظف
              </v-btn>

              <v-btn small depressed outlined color="#B45309" class="font-weight-bold rounded-lg" @click="openPriorityModal">
                <v-icon left x-small>mdi-flag-outline</v-icon>
                الأولوية
              </v-btn>

              <v-btn small depressed outlined color="#7E22CE" class="font-weight-bold rounded-lg" @click="openEscalateModal">
                <v-icon left x-small>mdi-arrow-up-bold-box-outline</v-icon>
                تصعيد
              </v-btn>

              <v-btn small depressed outlined color="#0369A1" class="font-weight-bold rounded-lg" @click="openLinkRefundModal">
                <v-icon left x-small>mdi-cash-refund</v-icon>
                ربط باسترداد
              </v-btn>
            </div>
          </div>
        </v-card>

        <!-- 2-Column Detailed Workspace Layout -->
        <v-row>
          <!-- Left Column (8 cols): Ticket Summary + Conversation + Internal Notes -->
          <v-col cols="12" lg="8">
            <!-- 1. Ticket Summary Card (Section 16) -->
            <v-card class="elevation-1 rounded-xl pa-4 mb-4 content-card">
              <div class="d-flex align-center justify-space-between mb-3 border-bottom pb-2">
                <div class="d-flex align-center">
                  <v-icon color="#082C69" class="me-2">mdi-card-text-outline</v-icon>
                  <span class="text-subtitle-1 font-weight-bold navy--text">ملخص التذكرة وبيانات العميل</span>
                </div>
                <span class="text-caption text-muted">تاريخ الإنشاء: {{ selectedTicket.created_at }}</span>
              </div>

              <!-- Customer & Trip Context Grid -->
              <v-row dense class="mb-3">
                <v-col cols="12" sm="6">
                  <div class="info-block">
                    <span class="info-label">اسم العميل</span>
                    <span class="info-value font-weight-bold">{{ selectedTicket.customer_name }}</span>
                  </div>
                </v-col>
                <v-col cols="12" sm="6">
                  <div class="info-block">
                    <span class="info-label">رقم الهاتف</span>
                    <span class="info-value mono-code">{{ selectedTicket.customer_phone }}</span>
                  </div>
                </v-col>
                <v-col cols="12" sm="6">
                  <div class="info-block">
                    <span class="info-label">رقم التذكرة / الحجز</span>
                    <span class="info-value mono-code font-weight-bold navy--text">{{ selectedTicket.booking_ref }}</span>
                  </div>
                </v-col>
                <v-col cols="12" sm="6">
                  <div class="info-block">
                    <span class="info-label">خط السير والرحلة</span>
                    <span class="info-value">{{ selectedTicket.route_name }} ({{ selectedTicket.trip_ref }})</span>
                  </div>
                </v-col>
              </v-row>

              <!-- Linked Refund Info Banner if exists -->
              <v-alert
                v-if="selectedTicket.refund_info"
                dense
                text
                color="#0369A1"
                class="rounded-lg mb-3"
              >
                <div class="d-flex align-center justify-space-between">
                  <div>
                    <v-icon small color="#0369A1" class="me-1">mdi-link-variant</v-icon>
                    <strong>مرتبطة بطلب استرداد رسمي:</strong>
                    <span class="mono-code ms-1 font-weight-bold">{{ selectedTicket.refund_info.request_code }}</span>
                    (المبلغ المطلوب: {{ selectedTicket.refund_info.requested_amount }} ج.م)
                  </div>
                  <v-btn
                    x-small
                    text
                    color="#0369A1"
                    class="font-weight-bold"
                    :to="`/customer-service/refunds/${selectedTicket.refund_info.id}`"
                  >
                    فتح طلب الاسترداد
                    <v-icon right x-small>mdi-arrow-left</v-icon>
                  </v-btn>
                </div>
              </v-alert>

              <!-- Original Customer Description Box -->
              <div class="reason-box pa-3 rounded-lg">
                <div class="d-flex align-center mb-1">
                  <v-icon small color="#082C69" class="me-1">mdi-comment-account-outline</v-icon>
                  <span class="text-caption font-weight-bold navy--text">نص رسالة الشكوى من العميل:</span>
                </div>
                <div class="text-body-2 text-dark message-raw-text">{{ selectedTicket.complaint_raw }}</div>
              </div>
            </v-card>

            <!-- 2. Messages & Internal Notes Workspace (Section 9 & 24) -->
            <v-card class="elevation-1 rounded-xl pa-4 mb-4 content-card">
              <div class="d-flex align-center justify-space-between mb-3 border-bottom pb-2">
                <div class="d-flex align-center">
                  <v-icon color="#082C69" class="me-2">mdi-forum-outline</v-icon>
                  <span class="text-subtitle-1 font-weight-bold navy--text">المحادثة والمتابعة والملاحظات</span>
                </div>

                <!-- Tabs: Customer Replies vs Internal Notes -->
                <v-btn-toggle v-model="messageTab" mandatory dense class="rounded-lg">
                  <v-btn small value="customer" class="px-3 font-weight-bold">
                    <v-icon left x-small color="#15803D">mdi-send</v-icon>
                    ردود العميل
                  </v-btn>
                  <v-btn small value="internal" class="px-3 font-weight-bold">
                    <v-icon left x-small color="#B45309">mdi-lock-outline</v-icon>
                    ملاحظات داخلية
                  </v-btn>
                </v-btn-toggle>
              </div>

              <!-- Messages List -->
              <div class="messages-wrapper position-relative">
                <div
                  ref="messagesContainer"
                  class="messages-container pa-2 mb-3"
                  @scroll="onMessagesScroll"
                >
                  <div v-if="filteredMessages.length === 0" class="text-center py-6 text-muted">
                    <v-icon size="36" color="#CBD5E1">mdi-message-text-outline</v-icon>
                    <div class="mt-2 text-body-2">لا توجد رسائل في هذا التبويب بعد.</div>
                  </div>

                  <div
                    v-for="msg in filteredMessages"
                    :key="msg.id"
                    class="message-bubble pa-3 rounded-xl mb-3"
                    :class="{
                      'message-internal': msg.is_internal,
                      'message-customer': !msg.is_internal && msg.sender_role === 1,
                      'message-staff': !msg.is_internal && msg.sender_role !== 1,
                    }"
                  >
                    <div class="d-flex align-center justify-space-between mb-2">
                      <div class="d-flex align-center">
                        <v-icon small :color="msg.is_internal ? '#B45309' : (msg.sender_role === 1 ? '#0F172A' : '#082C69')" class="me-1">
                          {{ msg.is_internal ? 'mdi-shield-account' : 'mdi-account' }}
                        </v-icon>
                        <span class="font-weight-bold text-caption text-dark">{{ msg.sender_name }}</span>
                        
                        <!-- Sender Badge -->
                        <span
                          v-if="msg.is_internal"
                          class="sender-badge sender-badge-internal ms-2"
                        >
                          ملاحظة سرية
                        </span>
                        <span
                          v-else-if="msg.sender_role === 1"
                          class="sender-badge sender-badge-customer ms-2"
                        >
                          العميل
                        </span>
                        <span
                          v-else
                          class="sender-badge sender-badge-staff ms-2"
                        >
                          خدمة العملاء
                        </span>
                      </div>
                      <span class="text-caption font-weight-medium message-timestamp">{{ msg.created_at }}</span>
                    </div>
                    <div class="text-body-2 text-dark font-weight-medium message-content-text">{{ msg.message }}</div>
                  </div>
                </div>

                <!-- Floating Indicator for New Messages Below -->
                <transition name="fade">
                  <v-btn
                    v-if="hasNewMessagesBelow"
                    small
                    rounded
                    color="#082C69"
                    class="white--text new-messages-indicator elevation-3"
                    @click="scrollToBottomSmooth"
                  >
                    <v-icon left x-small>mdi-arrow-down</v-icon>
                    رسالة جديدة ↓
                  </v-btn>
                </transition>
              </div>

              <!-- Message Input Form -->
              <div class="message-composer pa-3 rounded-xl bg-slate-light">
                <div class="d-flex align-center mb-2">
                  <v-icon x-small :color="messageTab === 'internal' ? '#B45309' : '#082C69'" class="me-1">
                    {{ messageTab === 'internal' ? 'mdi-lock' : 'mdi-send' }}
                  </v-icon>
                  <span class="text-caption font-weight-bold" :class="messageTab === 'internal' ? 'text-amber-darken-3' : 'navy--text'">
                    {{ messageTab === 'internal' ? 'إضافة ملاحظة داخلية (فريق خدمة العملاء فقط - لن يراها العميل):' : 'إرسال رد مرئي للعميل:' }}
                  </span>
                </div>

                <v-textarea
                  v-model="replyText"
                  rows="3"
                  dense
                  outlined
                  hide-details
                  class="bg-white rounded-lg mb-2"
                  :placeholder="messageTab === 'internal' ? 'اكتب ملاحظتك الداخلية هنا...' : 'اكتب ردك الموجه للعميل هنا...'"
                ></v-textarea>

                <div class="d-flex align-center justify-end">
                  <v-btn
                    depressed
                    :color="messageTab === 'internal' ? '#B45309' : '#082C69'"
                    class="white--text font-weight-bold rounded-lg"
                    :loading="replyLoading"
                    :disabled="!replyText.trim()"
                    @click="sendReply"
                  >
                    <v-icon left size="16">{{ messageTab === 'internal' ? 'mdi-content-save-outline' : 'mdi-send' }}</v-icon>
                    {{ messageTab === 'internal' ? 'حفظ الملاحظة الداخلية' : 'إرسال الرد' }}
                  </v-btn>
                </div>
              </div>
            </v-card>
          </v-col>

          <!-- Right Column (4 cols): SLA + Attachments + Timeline -->
          <v-col cols="12" lg="4">
            <!-- 1. SLA & Escalation Card (Section 21) -->
            <v-card class="elevation-1 rounded-xl pa-4 mb-4 content-card">
              <div class="d-flex align-center justify-space-between mb-3 border-bottom pb-2">
                <div class="d-flex align-center">
                  <v-icon color="#082C69" class="me-2">mdi-clock-check-outline</v-icon>
                  <span class="text-subtitle-2 font-weight-bold navy--text">معلومات SLA والتصعيد</span>
                </div>
              </div>

              <div class="info-block mb-2">
                <span class="info-label">موعد الاستجابة المستهدف (SLA)</span>
                <span class="info-value font-weight-bold">{{ selectedTicket.sla_due_formatted }}</span>
              </div>

              <div class="info-block mb-2">
                <span class="info-label">الجهة المعنية الحالية</span>
                <span class="info-value font-weight-bold navy--text">{{ selectedTicket.department_label }}</span>
              </div>

              <div class="info-block mb-2">
                <span class="info-label">الموظف المسؤول</span>
                <span class="info-value font-weight-bold">{{ selectedTicket.assigned_name }}</span>
              </div>

              <div class="info-block mb-2">
                <span class="info-label">قناة الورود</span>
                <span class="info-value">{{ selectedTicket.channel_label }}</span>
              </div>
            </v-card>

            <!-- 2. Attachments Card (Section 18) -->
            <v-card class="elevation-1 rounded-xl pa-4 mb-4 content-card">
              <div class="d-flex align-center justify-space-between mb-3 border-bottom pb-2">
                <div class="d-flex align-center">
                  <v-icon color="#082C69" class="me-2">mdi-paperclip</v-icon>
                  <span class="text-subtitle-2 font-weight-bold navy--text">المرفقات والوثائق</span>
                </div>
                <v-btn x-small text color="#082C69" class="font-weight-bold" @click="openAttachmentDialog">
                  <v-icon left x-small>mdi-plus</v-icon>
                  إضافة
                </v-btn>
              </div>

              <div v-if="!selectedTicket.attachments || selectedTicket.attachments.length === 0" class="text-center py-4 text-muted text-caption">
                لا توجد مرفقات مع هذه التذكرة
              </div>

              <v-list v-else dense class="pa-0">
                <v-list-item
                  v-for="att in selectedTicket.attachments"
                  :key="att.id"
                  class="attachment-item px-3 py-2 mb-2 rounded-lg"
                >
                  <v-list-item-avatar size="32" color="#E0E7FF" class="my-0 me-2">
                    <v-icon color="#082C69" size="18">mdi-file-document-outline</v-icon>
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium text-caption">{{ att.file_name }}</v-list-item-title>
                    <v-list-item-subtitle class="text-caption text-muted">{{ att.file_size_formatted }} • {{ att.created_at }}</v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action class="my-0">
                    <v-btn icon small :href="att.download_url" target="_blank" color="#082C69">
                      <v-icon size="18">mdi-download</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-card>

            <!-- 3. Audit Trail & Escalation History (Section 19) -->
            <v-card class="elevation-1 rounded-xl pa-4 mb-4 content-card">
              <div class="d-flex align-center justify-space-between mb-3 border-bottom pb-2">
                <div class="d-flex align-center">
                  <v-icon color="#082C69" class="me-2">mdi-history</v-icon>
                  <span class="text-subtitle-2 font-weight-bold navy--text">سجل المتابعة والتصعيد</span>
                </div>
              </div>

              <v-timeline dense align-top class="tickets-timeline pa-0">
                <v-timeline-item
                  v-for="h in selectedTicket.history"
                  :key="h.id"
                  small
                  :color="getTimelineColor(h.action)"
                  class="pb-3"
                >
                  <div class="text-caption font-weight-bold navy--text timeline-actor">{{ h.action_by_name }}</div>
                  <div class="text-caption text-dark font-weight-medium timeline-note">{{ h.note }}</div>
                  <div class="text-caption timeline-timestamp">{{ h.created_at }}</div>
                </v-timeline-item>
              </v-timeline>
            </v-card>
          </v-col>
        </v-row>
      </div>
    </v-fade-transition>

    <!-- Dialog: Change Status -->
    <v-dialog v-model="statusDialog" max-width="480">
      <v-card class="rounded-xl pa-4">
        <div class="text-h6 font-weight-bold navy--text mb-3">تغيير حالة التذكرة</div>
        <v-select
          v-model="newStatus"
          :items="statusOptions.filter(o => o.value)"
          outlined
          dense
          label="الحالة الجديدة"
          item-text="text"
          item-value="value"
          class="mb-2"
        ></v-select>
        <v-textarea
          v-model="statusNote"
          rows="2"
          outlined
          dense
          label="ملاحظة التغيير (اختياري)"
          placeholder="سبب تغيير الحالة..."
          class="mb-3"
        ></v-textarea>
        <div class="d-flex justify-end gap-2">
          <v-btn text @click="statusDialog = false">إلغاء</v-btn>
          <v-btn depressed color="#082C69" class="white--text font-weight-bold" :loading="actionLoading" @click="submitStatus">
            حفظ الحالة
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Dialog: Assign Staff -->
    <v-dialog v-model="assignDialog" max-width="480">
      <v-card class="rounded-xl pa-4">
        <div class="text-h6 font-weight-bold navy--text mb-3">تعيين الموظف المسؤول</div>
        <v-select
          v-model="newAssignee"
          :items="staffList"
          outlined
          dense
          label="اختر الموظف"
          item-text="name"
          item-value="id"
          class="mb-2"
        ></v-select>
        <v-textarea
          v-model="assignNote"
          rows="2"
          outlined
          dense
          label="ملاحظة التعيين (اختياري)"
          placeholder="تعليمات أو توجيه للموظف..."
          class="mb-3"
        ></v-textarea>
        <div class="d-flex justify-end gap-2">
          <v-btn text @click="assignDialog = false">إلغاء</v-btn>
          <v-btn depressed color="#082C69" class="white--text font-weight-bold" :loading="actionLoading" @click="submitAssign">
            تأكيد التعيين
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Dialog: Change Priority -->
    <v-dialog v-model="priorityDialog" max-width="480">
      <v-card class="rounded-xl pa-4">
        <div class="text-h6 font-weight-bold navy--text mb-3">تحديث أولوية التذكرة</div>
        <v-select
          v-model="newPriority"
          :items="priorityOptions.filter(o => o.value)"
          outlined
          dense
          label="مستوى الأولوية"
          item-text="text"
          item-value="value"
          class="mb-2"
        ></v-select>
        <v-textarea
          v-model="priorityNote"
          rows="2"
          outlined
          dense
          label="ملاحظة الأولوية (اختياري)"
          class="mb-3"
        ></v-textarea>
        <div class="d-flex justify-end gap-2">
          <v-btn text @click="priorityDialog = false">إلغاء</v-btn>
          <v-btn depressed color="#082C69" class="white--text font-weight-bold" :loading="actionLoading" @click="submitPriority">
            حفظ الأولوية
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Dialog: Escalate Department -->
    <v-dialog v-model="escalateDialog" max-width="480">
      <v-card class="rounded-xl pa-4">
        <div class="text-h6 font-weight-bold navy--text mb-3">تصعيد التذكرة إلى قسم آخر</div>
        <p class="text-caption text-muted mb-2">
          سيتم تحديث الجهة المعنية وتغيير حالة التذكرة إلى "تم التصعيد" وتوثيق الحركة في سجل المتابعة.
        </p>
        <v-select
          v-model="newDepartment"
          :items="departmentOptions.filter(o => o.value)"
          outlined
          dense
          label="القسم المستهدف"
          item-text="text"
          item-value="value"
          class="mb-2"
        ></v-select>
        <v-textarea
          v-model="escalateNote"
          rows="2"
          outlined
          dense
          label="سبب التصعيد وتفاصيل الإحالة"
          class="mb-3"
        ></v-textarea>
        <div class="d-flex justify-end gap-2">
          <v-btn text @click="escalateDialog = false">إلغاء</v-btn>
          <v-btn depressed color="#7E22CE" class="white--text font-weight-bold" :loading="actionLoading" @click="submitEscalate">
            تأكيد التصعيد
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Dialog: Link Refund Request -->
    <v-dialog v-model="linkRefundDialog" max-width="480">
      <v-card class="rounded-xl pa-4">
        <div class="text-h6 font-weight-bold navy--text mb-3">ربط التذكرة بطلب استرداد رسمي</div>
        <p class="text-caption text-muted mb-2">
          الربط هو للإشارة والمتابعة فقط ولا يترتب عليه أي تحريك مالي من شاشة التذاكر.
        </p>
        <v-text-field
          v-model="newRefundRequestId"
          type="number"
          outlined
          dense
          label="رقم طلب الاسترداد (ID)"
          placeholder="مثال: 3"
          class="mb-2"
        ></v-text-field>
        <div class="d-flex justify-end gap-2">
          <v-btn text @click="linkRefundDialog = false">إلغاء</v-btn>
          <v-btn depressed color="#0369A1" class="white--text font-weight-bold" :loading="actionLoading" @click="submitLinkRefund">
            تأكيد الربط
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Dialog: Upload Attachment -->
    <v-dialog v-model="attachmentDialog" max-width="480">
      <v-card class="rounded-xl pa-4">
        <div class="text-h6 font-weight-bold navy--text mb-3">إرفاق ملف أو مستند</div>
        <v-file-input
          v-model="attachmentFile"
          outlined
          dense
          label="اختر ملف المرفق"
          placeholder="اختر ملف..."
          prepend-icon="mdi-paperclip"
          class="mb-2"
        ></v-file-input>
        <v-select
          v-model="attachmentVisibility"
          :items="[
            { text: 'ملاحظة سرية داخلية (خدمة العملاء فقط)', value: 'internal' },
            { text: 'مرئي للعميل في التطبيق', value: 'customer' }
          ]"
          outlined
          dense
          label="مستوى الرؤية"
          item-text="text"
          item-value="value"
          class="mb-3"
        ></v-select>
        <div class="d-flex justify-end gap-2">
          <v-btn text @click="attachmentDialog = false">إلغاء</v-btn>
          <v-btn
            depressed
            color="#082C69"
            class="white--text font-weight-bold"
            :loading="actionLoading"
            :disabled="!attachmentFile"
            @click="submitUploadAttachment"
          >
            رفع الملف
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Snackbar for notifications -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="4000" top>
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">إغلاق</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import axios from '@/axios';

export default {
  name: 'CustomerServiceTickets',
  data() {
    return {
      loading: false,
      exportLoading: false,
      actionLoading: false,
      replyLoading: false,

      // Tickets List & Pagination
      tickets: [],
      currentPage: 1,
      lastPage: 1,
      perPage: 15,
      totalItems: 0,

      // KPI Summary
      summary: {
        total_open: 0,
        high_priority: 0,
        in_progress: 0,
        escalated_finance: 0,
        escalated_operations: 0,
        resolved_today: 0,
        total_all: 0,
      },

      // Filters
      filters: {
        search: '',
        status: '',
        category: '',
        priority: '',
        department: '',
        channel: '',
        assigned_to: '',
        date_from: '',
        date_to: '',
      },

      // Staff List for dropdowns
      staffList: [],

      // Selected Ticket
      selectedTicket: null,

      // Live Polling & Smart Scroll
      activityInterval: null,
      isActivityPolling: false,
      lastKnownMessageId: 0,
      lastKnownHistoryId: 0,
      lastKnownStatus: null,
      hasNewMessagesBelow: false,
      attentionTicketIds: [],

      // Messages & Notes Workspace
      messageTab: 'customer',
      replyText: '',

      // Dialog States
      statusDialog: false,
      newStatus: '',
      statusNote: '',

      assignDialog: false,
      newAssignee: null,
      assignNote: '',

      priorityDialog: false,
      newPriority: '',
      priorityNote: '',

      escalateDialog: false,
      newDepartment: '',
      escalateNote: '',

      linkRefundDialog: false,
      newRefundRequestId: '',

      attachmentDialog: false,
      attachmentFile: null,
      attachmentVisibility: 'internal',

      // Snackbar
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',

      // Dropdown Options
      statusOptions: [
        { text: 'جميع الحالات', value: '' },
        { text: 'جديدة', value: 'new' },
        { text: 'مفتوحة', value: 'open' },
        { text: 'قيد المعالجة', value: 'in_progress' },
        { text: 'بانتظار العميل', value: 'waiting_customer' },
        { text: 'بانتظار إجراء داخلي', value: 'waiting_internal' },
        { text: 'تم التصعيد', value: 'escalated' },
        { text: 'تم الحل', value: 'resolved' },
        { text: 'مغلقة', value: 'closed' },
      ],
      categoryOptions: [
        { text: 'جميع التصنيفات', value: '' },
        { text: 'شكوى', value: 'complaint' },
        { text: 'مقترح', value: 'suggestion' },
        { text: 'استفسار', value: 'inquiry' },
        { text: 'دعم فني', value: 'technical_support' },
        { text: 'متابعة استرداد', value: 'refund_followup' },
        { text: 'سلوك سائق', value: 'driver_behavior' },
        { text: 'مشكلة رحلة', value: 'trip_issue' },
        { text: 'مشكلة حجز', value: 'booking_issue' },
        { text: 'أخرى', value: 'other' },
      ],
      priorityOptions: [
        { text: 'جميع الأولويات', value: '' },
        { text: 'منخفضة', value: 'low' },
        { text: 'متوسطة', value: 'medium' },
        { text: 'عالية', value: 'high' },
        { text: 'عاجلة', value: 'urgent' },
      ],
      departmentOptions: [
        { text: 'جميع الأقسام', value: '' },
        { text: 'خدمة العملاء', value: 'customer_service' },
        { text: 'العمليات', value: 'operations' },
        { text: 'المالية', value: 'finance' },
      ],
      channelOptions: [
        { text: 'جميع القنوات', value: '' },
        { text: 'تطبيق العميل', value: 'mobile_app' },
        { text: 'خدمة العملاء', value: 'customer_service' },
        { text: 'الهاتف', value: 'phone' },
        { text: 'البريد الإلكتروني', value: 'email' },
      ],

      // Table Headers
      tableHeaders: [
        { text: '#', value: 'index', sortable: false, width: '40px' },
        { text: 'رقم التذكرة', value: 'ticket_code', sortable: false, width: '110px' },
        { text: 'العميل', value: 'customer_name', sortable: false },
        { text: 'رقم الحجز', value: 'booking_ref', sortable: false },
        { text: 'الرحلة / الخط', value: 'route_name', sortable: false },
        { text: 'التصنيف', value: 'category', sortable: false },
        { text: 'الأولوية', value: 'priority', sortable: false },
        { text: 'الحالة', value: 'status', sortable: false },
        { text: 'الجهة المعنية', value: 'department', sortable: false },
        { text: 'الموظف المسؤول', value: 'assigned_name', sortable: false },
        { text: 'تاريخ الإنشاء', value: 'created_at', sortable: false },
        { text: 'SLA', value: 'sla', sortable: false },
        { text: 'الإجراءات', value: 'actions', sortable: false, align: 'center', width: '90px' },
      ],
    };
  },
  computed: {
    staffOptions() {
      return [
        { id: '', name: 'جميع الموظفين' },
        { id: 'unassigned', name: 'غير معيّن' },
        ...this.staffList,
      ];
    },
    filteredMessages() {
      if (!this.selectedTicket || !this.selectedTicket.messages) return [];
      if (this.messageTab === 'internal') {
        return this.selectedTicket.messages.filter(m => m.is_internal);
      }
      return this.selectedTicket.messages.filter(m => !m.is_internal);
    },
  },
  mounted() {
    this.fetchTickets();
    this.fetchStaffList();
    window.addEventListener('beepgo:cs-activity', this.handleCsActivityEvent);
    this.checkDeepLink();
  },
  watch: {
    '$route.query'() {
      this.checkDeepLink();
    }
  },
  beforeDestroy() {
    this.stopTicketPolling();
    window.removeEventListener('beepgo:cs-activity', this.handleCsActivityEvent);
  },
  methods: {
    async checkDeepLink() {
      const q = this.$route.query;
      if (!q) return;
      const ticketId = q.id ? parseInt(q.id, 10) : null;
      const ticketCode = q.ticket ? q.ticket.trim() : null;

      if (ticketId) {
        if (!this.selectedTicket || this.selectedTicket.id !== ticketId) {
          await this.fetchTicketDetail(ticketId, true, true);
        }
      } else if (ticketCode) {
        if (!this.selectedTicket || this.selectedTicket.ticket_code !== ticketCode) {
          this.filters.search = ticketCode;
          await this.fetchTickets(true);
          const match = this.tickets.find(t => t.ticket_code === ticketCode);
          if (match) {
            await this.fetchTicketDetail(match.id, true, true);
          }
        }
      }
    },
    showSnackbar(text, color = 'success') {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    },
    async fetchStaffList() {
      try {
        const res = await axios.get('/customer-service/tickets/staff-list');
        if (res.data && res.data.success) {
          this.staffList = res.data.data || [];
        }
      } catch (err) {
        console.error('Error fetching staff list:', err);
      }
    },
    async fetchTickets(showLoading = true) {
      if (showLoading) this.loading = true;
      try {
        const params = {
          page: this.currentPage,
          per_page: this.perPage,
          ...this.buildFilterParams(),
        };

        const response = await axios.get('/customer-service/tickets', { params });
        const resData = response.data;

        this.tickets = resData.data || [];
        this.currentPage = resData.current_page || 1;
        this.lastPage = resData.last_page || 1;
        this.totalItems = resData.total || 0;
        if (resData.summary) {
          this.summary = resData.summary;
        }

        // If previously selected ticket is still in list, refresh its details quietly
        if (this.selectedTicket && showLoading) {
          this.fetchTicketDetail(this.selectedTicket.id, false, false);
        }
      } catch (err) {
        console.error('Error fetching tickets:', err);
        if (showLoading) this.showSnackbar('تعذر تحميل تذاكر الدعم والشكاوى', 'error');
      } finally {
        if (showLoading) this.loading = false;
      }
    },
    buildFilterParams() {
      const p = {};
      if (this.filters.search) p.search = this.filters.search;
      if (this.filters.status) p.status = this.filters.status;
      if (this.filters.category) p.category = this.filters.category;
      if (this.filters.priority) p.priority = this.filters.priority;
      if (this.filters.department) p.department = this.filters.department;
      if (this.filters.channel) p.channel = this.filters.channel;
      if (this.filters.assigned_to) p.assigned_to = this.filters.assigned_to;
      if (this.filters.date_from) p.date_from = this.filters.date_from;
      if (this.filters.date_to) p.date_to = this.filters.date_to;
      return p;
    },
    applyFilters() {
      this.currentPage = 1;
      this.fetchTickets();
    },
    resetFilters() {
      this.filters = {
        search: '',
        status: '',
        category: '',
        priority: '',
        department: '',
        channel: '',
        assigned_to: '',
        date_from: '',
        date_to: '',
      };
      this.currentPage = 1;
      this.fetchTickets();
    },
    filterByKpi(type) {
      this.resetFilters();
      if (type === 'priority_urgent') {
        this.filters.priority = 'urgent';
      } else if (type === 'status_in_progress') {
        this.filters.status = 'in_progress';
      } else if (type === 'dept_finance') {
        this.filters.department = 'finance';
      } else if (type === 'dept_operations') {
        this.filters.department = 'operations';
      } else if (type === 'status_resolved') {
        this.filters.status = 'resolved';
      }
      this.applyFilters();
    },
    onPageChange(page) {
      this.currentPage = page;
      this.fetchTickets();
    },
    async selectTicket(item) {
      if (!item) return;
      this.attentionTicketIds = this.attentionTicketIds.filter(id => id !== item.id);
      await this.fetchTicketDetail(item.id, true, true);
    },
    async fetchTicketDetail(id, scrollTo = false, isUserSelection = false) {
      try {
        const res = await axios.get(`/customer-service/tickets/${id}`);
        if (res.data && res.data.success) {
          const data = res.data.data;
          this.selectedTicket = data;
          if (isUserSelection) {
            this.replyText = '';
            this.hasNewMessagesBelow = false;
          }
          this.initTicketTracking(data);
          this.startTicketPolling();

          if (scrollTo && this.$refs.workspaceSection) {
            this.$nextTick(() => {
              this.$refs.workspaceSection.scrollIntoView({ behavior: 'smooth' });
              this.scrollToBottomSmooth();
            });
          }
        }
      } catch (err) {
        console.error('Error fetching ticket detail:', err);
        this.showSnackbar('تعذر جلب تفاصيل التذكرة', 'error');
      }
    },
    initTicketTracking(ticket) {
      if (!ticket) return;
      const msgs = ticket.messages || [];
      const history = ticket.history || [];
      this.lastKnownMessageId = msgs.length ? Math.max(...msgs.map(m => m.id)) : 0;
      this.lastKnownHistoryId = history.length ? Math.max(...history.map(h => h.id)) : 0;
      this.lastKnownStatus = ticket.status_key;
    },
    startTicketPolling() {
      this.stopTicketPolling();
      this.activityInterval = window.setInterval(() => {
        this.checkTicketActivity();
      }, 2000);
    },
    stopTicketPolling() {
      if (this.activityInterval) {
        clearInterval(this.activityInterval);
        this.activityInterval = null;
      }
    },
    async checkTicketActivity() {
      if (!this.selectedTicket || !this.selectedTicket.id) return;
      if (document.hidden) return; // Tab paused in background
      if (this.isActivityPolling) return; // Request overlap guard

      this.isActivityPolling = true;
      try {
        const ticketId = this.selectedTicket.id;
        const res = await axios.get(`/customer-service/tickets/${ticketId}/activity`);
        if (res.data && res.data.success) {
          const act = res.data.data;
          const hasNewMsg = act.latest_message_id > this.lastKnownMessageId;
          const hasNewHist = act.latest_history_id > this.lastKnownHistoryId;
          const hasStatusChange = act.status_key !== this.lastKnownStatus;

          if (hasNewMsg || hasNewHist || hasStatusChange) {
            await this.refreshSelectedTicketQuietly(ticketId, act);
          }
        }
      } catch (e) {
        // Silent fail for polling to avoid interrupting staff
      } finally {
        this.isActivityPolling = false;
      }
    },
    async refreshSelectedTicketQuietly(ticketId, act) {
      try {
        const res = await axios.get(`/customer-service/tickets/${ticketId}`);
        if (!res.data || !res.data.success) return;
        // Verify user didn't switch tickets during request
        if (!this.selectedTicket || this.selectedTicket.id !== ticketId) return;

        const newDetail = res.data.data;
        const newMessages = newDetail.messages || [];
        const container = this.$refs.messagesContainer;

        // Check if user was already near the bottom before DOM update
        let isNearBottom = true;
        if (container) {
          const scrollDiff = container.scrollHeight - container.scrollTop - container.clientHeight;
          isNearBottom = scrollDiff < 60;
        }

        // Deduplication & incoming customer message sound gate
        let customerReplyDetected = false;
        const soundKeys = [];

        newMessages.forEach(m => {
          if (m.id > this.lastKnownMessageId) {
            // Customer message: sender_role === 1 and not internal
            if (m.sender_role === 1 && !m.is_internal) {
              customerReplyDetected = true;
              soundKeys.push('msg_' + m.id);
              soundKeys.push('ticket_' + ticketId + '_msg_' + m.id);
            }
          }
        });

        // Update selected ticket in place without clearing replyText or changing messageTab
        this.selectedTicket.messages = newMessages;
        this.selectedTicket.history = newDetail.history || [];
        this.selectedTicket.status = newDetail.status;
        this.selectedTicket.status_key = newDetail.status_key;
        this.selectedTicket.status_label = newDetail.status_label;
        this.selectedTicket.status_color = newDetail.status_color;
        this.selectedTicket.assigned_to = newDetail.assigned_to;
        this.selectedTicket.assigned_name = newDetail.assigned_name;
        this.selectedTicket.updated_at = newDetail.updated_at;
        this.selectedTicket.sla_status = newDetail.sla_status;
        this.selectedTicket.sla_due_formatted = newDetail.sla_due_formatted;

        // Update tracking baselines
        this.lastKnownMessageId = newMessages.length ? Math.max(...newMessages.map(m => m.id)) : this.lastKnownMessageId;
        const newHist = newDetail.history || [];
        this.lastKnownHistoryId = newHist.length ? Math.max(...newHist.map(h => h.id)) : this.lastKnownHistoryId;
        this.lastKnownStatus = newDetail.status_key;

        // If customer reply was detected, trigger authoritative sound gate
        if (customerReplyDetected && soundKeys.length > 0) {
          if (window.__beepgoAuthoritativeSoundGate) {
            window.__beepgoAuthoritativeSoundGate(soundKeys);
          }
        }

        // Smart scroll handling
        this.$nextTick(() => {
          if (isNearBottom) {
            this.scrollToBottomSmooth();
            this.hasNewMessagesBelow = false;
          } else if (customerReplyDetected) {
            this.hasNewMessagesBelow = true;
          }
        });

        // Also update table row in background so table matches
        const tableItem = this.tickets.find(t => t.id === ticketId);
        if (tableItem) {
          tableItem.status = newDetail.status;
          tableItem.status_key = newDetail.status_key;
          tableItem.status_label = newDetail.status_label;
          tableItem.status_color = newDetail.status_color;
          tableItem.assigned_to = newDetail.assigned_to;
          tableItem.assigned_name = newDetail.assigned_name;
          tableItem.updated_at = newDetail.updated_at;
        }
      } catch (e) {
        console.warn('Quiet ticket refresh error:', e);
      }
    },
    scrollToBottomSmooth() {
      this.$nextTick(() => {
        const c = this.$refs.messagesContainer;
        if (c) {
          c.scrollTo({ top: c.scrollHeight, behavior: 'smooth' });
          this.hasNewMessagesBelow = false;
        }
      });
    },
    onMessagesScroll() {
      const c = this.$refs.messagesContainer;
      if (!c) return;
      const scrollDiff = c.scrollHeight - c.scrollTop - c.clientHeight;
      if (scrollDiff < 40) {
        this.hasNewMessagesBelow = false;
      }
    },
    handleCsActivityEvent(e) {
      if (e && e.detail && e.detail.ticketId) {
        const tId = e.detail.ticketId;
        if (this.selectedTicket && this.selectedTicket.id === tId) {
          this.checkTicketActivity();
        } else {
          if (!this.attentionTicketIds.includes(tId)) {
            this.attentionTicketIds.push(tId);
          }
        }
      }
    },
    async sendReply() {
      if (!this.selectedTicket || !this.replyText.trim()) return;

      this.replyLoading = true;
      try {
        const res = await axios.post(`/customer-service/tickets/${this.selectedTicket.id}/reply`, {
          message: this.replyText.trim(),
          visibility: this.messageTab,
        });

        if (res.data && res.data.success) {
          this.selectedTicket = res.data.data;
          this.initTicketTracking(res.data.data);
          this.replyText = '';
          this.scrollToBottomSmooth();
          this.showSnackbar(res.data.message || 'تم الإرسال بنجاح', 'success');
          this.fetchTickets(false);
        }
      } catch (err) {
        const msg = (err.response && err.response.data && err.response.data.message) || 'فشل إرسال الرد';
        this.showSnackbar(msg, 'error');
      } finally {
        this.replyLoading = false;
      }
    },
    openStatusModal() {
      if (!this.selectedTicket) return;
      this.newStatus = this.selectedTicket.status;
      this.statusNote = '';
      this.statusDialog = true;
    },
    async submitStatus() {
      if (!this.selectedTicket || !this.newStatus) return;
      this.actionLoading = true;
      try {
        const res = await axios.post(`/customer-service/tickets/${this.selectedTicket.id}/status`, {
          status: this.newStatus,
          note: this.statusNote.trim(),
        });
        if (res.data && res.data.success) {
          this.selectedTicket = res.data.data;
          this.initTicketTracking(res.data.data);
          this.statusDialog = false;
          this.showSnackbar('تم تحديث حالة التذكرة بنجاح', 'success');
          this.fetchTickets(false);
        }
      } catch (err) {
        const msg = (err.response && err.response.data && err.response.data.message) || 'فشل تحديث الحالة';
        this.showSnackbar(msg, 'error');
      } finally {
        this.actionLoading = false;
      }
    },
    openAssignModal() {
      if (!this.selectedTicket) return;
      this.newAssignee = this.selectedTicket.assigned_to;
      this.assignNote = '';
      this.assignDialog = true;
    },
    async submitAssign() {
      if (!this.selectedTicket) return;
      this.actionLoading = true;
      try {
        const res = await axios.post(`/customer-service/tickets/${this.selectedTicket.id}/assign`, {
          assigned_to: this.newAssignee || null,
          note: this.assignNote.trim(),
        });
        if (res.data && res.data.success) {
          this.selectedTicket = res.data.data;
          this.initTicketTracking(res.data.data);
          this.assignDialog = false;
          this.showSnackbar('تم تعيين الموظف بنجاح', 'success');
          this.fetchTickets(false);
        }
      } catch (err) {
        const msg = (err.response && err.response.data && err.response.data.message) || 'فشل تعيين الموظف';
        this.showSnackbar(msg, 'error');
      } finally {
        this.actionLoading = false;
      }
    },
    openPriorityModal() {
      if (!this.selectedTicket) return;
      this.newPriority = this.selectedTicket.priority;
      this.priorityNote = '';
      this.priorityDialog = true;
    },
    async submitPriority() {
      if (!this.selectedTicket || !this.newPriority) return;
      this.actionLoading = true;
      try {
        const res = await axios.post(`/customer-service/tickets/${this.selectedTicket.id}/priority`, {
          priority: this.newPriority,
          note: this.priorityNote.trim(),
        });
        if (res.data && res.data.success) {
          this.selectedTicket = res.data.data;
          this.initTicketTracking(res.data.data);
          this.priorityDialog = false;
          this.showSnackbar('تم تحديث الأولوية بنجاح', 'success');
          this.fetchTickets(false);
        }
      } catch (err) {
        const msg = (err.response && err.response.data && err.response.data.message) || 'فشل تحديث الأولوية';
        this.showSnackbar(msg, 'error');
      } finally {
        this.actionLoading = false;
      }
    },
    openEscalateModal() {
      if (!this.selectedTicket) return;
      this.newDepartment = this.selectedTicket.department === 'customer_service' ? 'operations' : this.selectedTicket.department;
      this.escalateNote = '';
      this.escalateDialog = true;
    },
    async submitEscalate() {
      if (!this.selectedTicket || !this.newDepartment) return;
      this.actionLoading = true;
      try {
        const res = await axios.post(`/customer-service/tickets/${this.selectedTicket.id}/escalate`, {
          department: this.newDepartment,
          note: this.escalateNote.trim(),
        });
        if (res.data && res.data.success) {
          this.selectedTicket = res.data.data;
          this.initTicketTracking(res.data.data);
          this.escalateDialog = false;
          this.showSnackbar('تم تصعيد التذكرة بنجاح', 'success');
          this.fetchTickets(false);
        }
      } catch (err) {
        const msg = (err.response && err.response.data && err.response.data.message) || 'فشل تصعيد التذكرة';
        this.showSnackbar(msg, 'error');
      } finally {
        this.actionLoading = false;
      }
    },
    openLinkRefundModal() {
      if (!this.selectedTicket) return;
      this.newRefundRequestId = this.selectedTicket.refund_info ? this.selectedTicket.refund_info.id : '';
      this.linkRefundDialog = true;
    },
    async submitLinkRefund() {
      if (!this.selectedTicket) return;
      this.actionLoading = true;
      try {
        const res = await axios.post(`/customer-service/tickets/${this.selectedTicket.id}/link-refund`, {
          refund_request_id: this.newRefundRequestId ? parseInt(this.newRefundRequestId) : null,
        });
        if (res.data && res.data.success) {
          this.selectedTicket = res.data.data;
          this.initTicketTracking(res.data.data);
          this.linkRefundDialog = false;
          this.showSnackbar('تم ربط طلب الاسترداد بنجاح', 'success');
          this.fetchTickets(false);
        }
      } catch (err) {
        const msg = (err.response && err.response.data && err.response.data.message) || 'فشل ربط طلب الاسترداد';
        this.showSnackbar(msg, 'error');
      } finally {
        this.actionLoading = false;
      }
    },
    openAttachmentDialog() {
      this.attachmentFile = null;
      this.attachmentVisibility = 'internal';
      this.attachmentDialog = true;
    },
    async submitUploadAttachment() {
      if (!this.selectedTicket || !this.attachmentFile) return;
      this.actionLoading = true;
      try {
        const formData = new FormData();
        formData.append('file', this.attachmentFile);
        formData.append('visibility', this.attachmentVisibility);

        const res = await axios.post(`/customer-service/tickets/${this.selectedTicket.id}/attachments`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        if (res.data && res.data.success) {
          this.attachmentDialog = false;
          this.attachmentFile = null;
          this.showSnackbar('تم رفع المرفق بنجاح', 'success');
          this.fetchTicketDetail(this.selectedTicket.id, false);
        }
      } catch (err) {
        const msg = (err.response && err.response.data && err.response.data.message) || 'فشل رفع المرفق';
        this.showSnackbar(msg, 'error');
      } finally {
        this.actionLoading = false;
      }
    },
    async exportCsv() {
      this.exportLoading = true;
      try {
        const params = this.buildFilterParams();
        const response = await axios.get('/customer-service/tickets/export-csv', {
          params,
          responseType: 'blob',
        });
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv;charset=utf-8;' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `beepgo-tickets-${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (err) {
        console.error('Error exporting CSV:', err);
        this.showSnackbar('فشل في تصدير التذاكر', 'error');
      } finally {
        this.exportLoading = false;
      }
    },
    getTimelineColor(action) {
      const colors = {
        created: '#0369A1',
        status_changed: '#1D4ED8',
        assigned: '#7E22CE',
        priority_changed: '#B45309',
        escalated: '#B91C1C',
        reply_sent: '#15803D',
        internal_note: '#64748B',
        refund_linked: '#0D9488',
      };
      return colors[action] || '#64748B';
    },
    getCategoryBadgeStyle(category) {
      const styles = {
        technical_support: { bg: '#EDE9FE', text: '#5B21B6', border: '#C4B5FD' }, // دعم فني
        refund_followup: { bg: '#E0F2FE', text: '#0369A1', border: '#7DD3FC' },   // متابعة استرداد
        booking_issue: { bg: '#FEF3C7', text: '#92400E', border: '#FCD34D' },     // مشكلة حجز
        payment_issue: { bg: '#FEE2E2', text: '#991B1B', border: '#FCA5A5' },     // مشكلة دفع
        driver_complaint: { bg: '#FFEDD5', text: '#9A3412', border: '#FDBA74' },  // شكوى ضد سائق
        trip_service_issue: { bg: '#FCE7F3', text: '#9D174D', border: '#F9A8D4' },// مشكلة في الرحلة
        complaint: { bg: '#F1F5F9', text: '#0F172A', border: '#CBD5E1' },         // شكوى عامة
        inquiry: { bg: '#E0F7FA', text: '#006064', border: '#80DEEA' },           // استفسار
        suggestion: { bg: '#ECFDF5', text: '#065F46', border: '#6EE7B7' },        // اقتراح
        other: { bg: '#F1F5F9', text: '#1E293B', border: '#CBD5E1' },             // أخرى
      };
      const s = styles[category] || { bg: '#F1F5F9', text: '#1E293B', border: '#CBD5E1' };
      return {
        backgroundColor: s.bg,
        color: s.text,
        border: `1px solid ${s.border}`,
      };
    },
    getSlaBadgeStyle(item) {
      if (item.sla_status === 'overdue') {
        return {
          backgroundColor: '#FEE2E2',
          color: '#991B1B',
          border: '1px solid #FCA5A5',
        };
      }
      if (item.sla_status === 'on_track') {
        return {
          backgroundColor: '#DCFCE7',
          color: '#166534',
          border: '1px solid #86EFAC',
        };
      }
      return {
        backgroundColor: '#F1F5F9',
        color: '#334155',
        border: '1px solid #CBD5E1',
      };
    },
  },
};
</script>

<style scoped>
.cs-tickets-container {
  font-family: 'Cairo', sans-serif !important;
  color: #1E293B;
  min-height: 100vh;
}

.navy--text {
  color: #082C69 !important;
}

.text-dark {
  color: #0F172A !important;
}

.text-muted {
  color: #64748B !important;
}

.text-emerald {
  color: #15803D !important;
}

.text-danger {
  color: #B91C1C !important;
}

.mono-code {
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace !important;
  letter-spacing: 0.5px;
}

.border-bottom {
  border-bottom: 1px solid #F1F5F9 !important;
}

.border-top {
  border-top: 1px solid #F1F5F9 !important;
}

.border-primary-top {
  border-top: 4px solid #082C69 !important;
}

.gap-2 {
  gap: 8px;
}

/* KPI Cards */
.kpi-card {
  border: 1px solid #E2E8F0 !important;
  transition: all 0.2s ease-in-out;
}

.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(8, 44, 105, 0.08) !important;
}

.kpi-card-active {
  border: 2px solid #082C69 !important;
  background-color: #F8FAFC !important;
}

/* Badges */
.badge-chip {
  display: inline-block;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  text-align: center;
  white-space: nowrap;
  opacity: 1 !important;
}

/* Ticket count badge */
.ticket-count-badge {
  display: inline-block;
  background-color: #EBF3FE;
  color: #082C69;
  border: 1px solid #BFDBFE;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 700;
  opacity: 1 !important;
}

/* Category Badge */
.category-badge {
  display: inline-block;
  padding: 3px 9px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700 !important;
  text-align: center;
  white-space: nowrap;
  opacity: 1 !important;
  letter-spacing: 0.2px;
}

/* SLA Badge */
.sla-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700 !important;
  text-align: center;
  white-space: nowrap;
  opacity: 1 !important;
}

/* Sender Badges */
.sender-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  text-align: center;
  white-space: nowrap;
  opacity: 1 !important;
}

.sender-badge-internal {
  background-color: #FEF3C7 !important;
  color: #92400E !important;
  border: 1px solid #FCD34D !important;
}

.sender-badge-customer {
  background-color: #E2E8F0 !important;
  color: #1E293B !important;
  border: 1px solid #CBD5E1 !important;
}

.sender-badge-staff {
  background-color: #DBEAFE !important;
  color: #082C69 !important;
  border: 1px solid #93C5FD !important;
}

/* Tables */
.tickets-table {
  border-radius: 0 0 12px 12px;
}

.tickets-table >>> th {
  background-color: #F8FAFC !important;
  color: #334155 !important;
  font-weight: 700 !important;
  font-size: 12px !important;
  white-space: nowrap;
}

.tickets-table >>> tr:hover {
  background-color: #F1F5F9 !important;
  cursor: pointer;
}

/* Info blocks */
.info-block {
  display: flex;
  flex-direction: column;
}

.info-label {
  font-size: 11px;
  color: #475569;
  font-weight: 600;
}

.info-value {
  font-size: 13px;
  color: #0F172A;
  font-weight: 600;
}

/* Reason & Messages */
.reason-box {
  background-color: #F8FAFC;
  border: 1px solid #CBD5E1;
}

.message-raw-text {
  white-space: pre-wrap;
  line-height: 1.6;
  color: #0F172A;
  font-weight: 500;
}

.messages-container {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #CBD5E1;
  border-radius: 12px;
  background-color: #F8FAFC;
}

/* Conversation Cards */
.message-bubble {
  border-radius: 12px;
  transition: all 0.15s ease;
}

.message-customer {
  background-color: #FFFFFF !important;
  border: 1px solid #CBD5E1 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.message-staff {
  background-color: #EFF6FF !important;
  border: 1px solid #93C5FD !important;
  box-shadow: 0 1px 3px rgba(8, 44, 105, 0.06);
}

.message-internal {
  background-color: #FFFBEB !important;
  border: 1px solid #FCD34D !important;
  box-shadow: 0 1px 3px rgba(180, 83, 9, 0.05);
}

.message-content-text {
  color: #0F172A !important;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
  opacity: 1 !important;
}

.message-timestamp {
  color: #475569 !important;
  font-size: 11px;
  font-weight: 600;
  opacity: 1 !important;
}

/* Timeline elements */
.timeline-actor {
  color: #082C69 !important;
  font-weight: 700 !important;
  font-size: 12px;
}

.timeline-note {
  color: #1E293B !important;
  font-weight: 500 !important;
  font-size: 12px;
  line-height: 1.5;
  margin-top: 2px;
  margin-bottom: 2px;
}

.timeline-timestamp {
  color: #475569 !important;
  font-weight: 600 !important;
  font-size: 11px;
  opacity: 1 !important;
}

.attachment-item {
  background-color: #F8FAFC;
  border: 1px solid #CBD5E1;
}

.bg-slate-light {
  background-color: #F8FAFC !important;
}

/* Live Polling & Smart Scroll UI */
.messages-wrapper {
  position: relative;
}

.new-messages-indicator {
  position: absolute;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  box-shadow: 0 4px 12px rgba(8, 44, 105, 0.3) !important;
  font-weight: 700 !important;
  font-size: 12px !important;
  letter-spacing: 0.3px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
  transform: translate(-50%, 8px);
}

.attention-badge {
  display: inline-block;
  background-color: #EF4444;
  color: #FFFFFF;
  font-size: 10px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 6px;
  animation: pulse-attention 2s infinite;
}

@keyframes pulse-attention {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.08);
    opacity: 0.85;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}
</style>
